import React, { RefObject, useEffect, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import chatInputIcon from "../../../../assets/imgs/chat_input_icon.svg";
import chatInputBlackIcon from "../../../../assets/imgs/chat_input_black_icon.svg";

interface FollowUpInputProps {
  followUpRef: RefObject<HTMLTextAreaElement>;
  containerRef: RefObject<HTMLDivElement>;
  followUpInput: string;
  isFollowUpFocused: boolean;
  showFollowUpInput: boolean;
  handleFollowUpChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleFollowUpFocus: () => void;
  handleFollowUpBlur: () => void;
  handleFollowUpSubmit: () => void;
}

const FollowUpInput: React.FC<FollowUpInputProps> = ({
  followUpRef,
  containerRef,
  followUpInput,
  isFollowUpFocused,
  showFollowUpInput,
  handleFollowUpChange,
  handleFollowUpFocus,
  handleFollowUpBlur,
  handleFollowUpSubmit,
}) => {
  // Function to handle key press events
  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Check if Enter key is pressed without Shift key
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent the default action (new line)
      if (followUpInput.trim()) {
        handleFollowUpSubmit(); // Call the submit function
      }
    }
  };

  // Base style for follow up input with dynamic properties based on focus state
  const baseStyleFollowUp: React.CSSProperties = {
    width: "100%",
    minHeight: isFollowUpFocused ? "52px" : "52px", // Min height - fixed when not focused
    maxHeight: isFollowUpFocused ? "120px" : "52px", // Max height - fixed when not focused
    height: "52px", // Initial height always 52px
    padding: isFollowUpFocused ? "16px 0" : "16px", // Remove left/right padding when focused
    paddingLeft: isFollowUpFocused ? "16px" : "24px", // Add more left padding when not focused (24px instead of 16px)
    paddingRight: isFollowUpFocused ? "16px" : undefined, // Add back right padding when focused
    background: "#1A1E2B",
    borderTopLeftRadius: isFollowUpFocused ? "0px" : "50px",
    borderBottomLeftRadius: isFollowUpFocused ? "0px" : "50px",
    borderTopRightRadius:  "0px",
    borderBottomRightRadius:  "0px",
    color: "#ffffff",
    fontSize: "14px",
    overflowY: isFollowUpFocused ? "auto" : "hidden", // Enable scrolling only when focused
  };

  // Effect to adjust textarea height based on focus state
  useEffect(() => {
    if (followUpRef.current) {
      if (isFollowUpFocused) {
        // Reset height to auto to get the correct scrollHeight
        followUpRef.current.style.height = "auto";
        // Get the scroll height (content height)
        const scrollHeight = followUpRef.current.scrollHeight;
        // Calculate new height (clamped between min and max)
        const minHeight = 52; // Minimum height (matches the default height)
        const maxHeight = 120; // Maximum height before scrolling begins
        const newHeight = Math.min(
          Math.max(scrollHeight, minHeight),
          maxHeight,
        );
        // Apply the new height
        followUpRef.current.style.height = `${newHeight}px`;
      } else {
        followUpRef.current.style.height = "52px";
      }
    }
  }, [isFollowUpFocused, followUpRef, followUpInput]);

  // Animation variants for fade in/out
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  return (
    <AnimatePresence>
      {showFollowUpInput && (
        <motion.div
          ref={containerRef}
          className={`flex fixed ${isFollowUpFocused ? "w-full left-0" : "w-[94%] left-[3%]"} z-50 ${isFollowUpFocused ? "bottom-[0px]" : "bottom-[35px]"}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            willChange: "width, left, bottom, transform, opacity",
          }}
        >
          <textarea
            ref={followUpRef}
            className="html-textarea transition-all duration-300"
            placeholder="Ask a follow up question…"
            value={followUpInput}
            onChange={handleFollowUpChange}
            onFocus={handleFollowUpFocus}
            onKeyDown={handleKeyPress}
            onBlur={(e) => {
              // Only call handleFollowUpBlur if the click is outside
              // the container (handled by the useEffect in parent)
              if (
                !containerRef.current?.contains(e.relatedTarget as Node) &&
                e.relatedTarget !== document.getElementById("button-chat")
              ) {
                e.preventDefault(); // Prevent default blur behavior
              }
            }}
            style={{
              ...baseStyleFollowUp,
              resize: "none",
              transition: "all 0.3s ease-in-out",
              transformOrigin: "center",
            }}
          />

          <div
            id="button-chat"
            onMouseDown={(event) => event.preventDefault()}
            className={`flex pr-4 items-center transition-all duration-300`}
            style={{
              background: "#1A1E2B",
              borderTopRightRadius: isFollowUpFocused ? "0px" : "50px",
              borderBottomRightRadius: isFollowUpFocused ? "0px" : "50px",
              height: followUpRef.current
                ? followUpRef.current.style.height
                : "52px", // Match height of textarea
              display: "flex",
              alignItems: "center",
              transition: "all 0.3s ease-in-out",
              
            }}
          >
            <div
              onClick={followUpInput.trim() ? handleFollowUpSubmit : undefined}
              className={`cursor-pointer w-[24px] h-[24px] flex items-center justify-center rounded-full ${
                followUpInput.trim() ? "bg-white" : "bg-white/10"
              }`}
            >
              <img
                src={followUpInput.trim() ? chatInputBlackIcon : chatInputIcon}
                alt="search"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FollowUpInput;
